<template>
  <ValidationProvider
    v-slot="{ errors }"
    :name="name"
    :rules="resolvedRules"
    :vid="name"
  >
    <AppFormField
      :label="label"
      :description="description"
      :top-description="topDescription"
      :error-message="errors[0]"
      :required="required"
      :class-name="className"
      :label-class-name="labelClassName"
    >
      <div class="app-select" :class="inputClassName">
        <v-menu
          v-model="menuOpen"
          :disabled="disabled || readOnly"
          offset-y
          :nudge-bottom="4"
          content-class="app-select__menu-content"
          :close-on-content-click="false"
        >
          <template #activator="{ on, attrs }">
            <button
              v-bind="attrs"
              v-on="on"
              type="button"
              class="app-select__trigger"
              :class="{
                'app-select__trigger--open': menuOpen,
                'app-select__trigger--disabled': disabled || readOnly,
              }"
              :disabled="disabled || readOnly"
            >
              <span
                class="app-select__trigger-text"
                :class="{ 'app-select__trigger-text--placeholder': !selectedOption }"
              >
                <slot name="trigger" :option="selectedOption" :value="value">
                  {{ selectedOption ? selectedOption.label : placeholder }}
                </slot>
              </span>
              <i class="ri-arrow-down-s-line app-select__chevron" aria-hidden="true" />
            </button>
          </template>

          <div class="app-select__menu-wrap">
            <div v-if="searchable" class="app-select__search">
              <i class="ri-search-line" aria-hidden="true" />
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                class="app-select__search-input"
                :placeholder="searchPlaceholder"
                @input="onSearchInput"
              />
            </div>

            <div
              class="app-select__menu"
              @scroll.passive="onMenuScroll"
            >
              <button
                v-for="option in normalizedOptions"
                :key="String(option.value)"
                type="button"
                class="app-select__menu-item"
                :class="{ 'app-select__menu-item--selected': option.value === value }"
                @click="selectOption(option)"
              >
                {{ option.label }}
              </button>
              <p
                v-if="isLoadingMore"
                class="app-select__menu-status"
              >
                Loading...
              </p>
              <p
                v-else-if="!normalizedOptions.length && isSearching"
                class="app-select__menu-status"
              >
                {{ searchEmptyText }}
              </p>
              <p
                v-else-if="!normalizedOptions.length"
                class="app-select__menu-status"
              >
                {{ emptyText }}
              </p>
            </div>
          </div>
        </v-menu>
      </div>
    </AppFormField>
  </ValidationProvider>
</template>

<script>
import AppFormField from './fields/AppFormField.vue';
import { buildRulesString } from './formRules';

export default {
  name: 'AppSelect',
  components: { AppFormField },
  props: {
    name: { type: String, required: true },
    value: { type: [String, Number], default: '' },
    label: { type: String, default: '' },
    description: { type: String, default: '' },
    topDescription: { type: String, default: '' },
    placeholder: { type: String, default: 'Select' },
    emptyText: { type: String, default: 'No options found' },
    options: { type: Array, default: () => [] },
    rules: { type: [String, Object], default: null },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    className: { type: String, default: '' },
    labelClassName: { type: String, default: '' },
    inputClassName: { type: String, default: '' },
    hasMore: { type: Boolean, default: false },
    isLoadingMore: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search' },
    searchEmptyText: { type: String, default: 'No results found' },
  },
  data() {
    return {
      menuOpen: false,
      searchQuery: '',
      searchDebounceTimer: null,
    };
  },
  computed: {
    normalizedOptions() {
      return (this.options || []).map((option) => ({
        label: option.label ?? option.name ?? option.text ?? String(option.value ?? option.id ?? ''),
        value: option.value ?? option.id ?? option.label,
      }));
    },
    isSearching() {
      return this.searchable && Boolean(this.searchQuery.trim());
    },
    selectedOption() {
      if (this.value === null || this.value === undefined || this.value === '') {
        return null;
      }
      return this.normalizedOptions.find((option) => option.value === this.value) || null;
    },
    resolvedRules() {
      if (typeof this.rules === 'string') return this.rules;
      return buildRulesString({
        type: 'select',
        rules: this.rules || {},
        required: this.required,
      });
    },
  },
  watch: {
    menuOpen(open) {
      if (open) {
        if (this.searchable) {
          this.$nextTick(() => this.$refs.searchInput?.focus());
        }
        return;
      }
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      const hadQuery = Boolean(this.searchQuery.trim());
      this.searchQuery = '';
      // Let a paginated parent reset back to the unfiltered first page for
      // next time this menu opens.
      if (this.searchable && hadQuery) this.$emit('search', '');
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    selectOption(option) {
      this.$emit('input', option.value);
      this.menuOpen = false;
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.$emit('search', this.searchQuery.trim());
      }, 300);
    },
    onMenuScroll(event) {
      if (!this.hasMore || this.isLoadingMore) return;
      const container = event?.target;
      if (!container) return;
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom > 48) return;
      this.$emit('load-more');
    },
  },
};
</script>

<style scoped>
.app-select {
  width: 100%;
}

.app-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: 44px;
  padding: 12px 16px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.app-select__trigger:hover:not(:disabled) {
  border-color: #d9d9d9;
}

.app-select__trigger--open,
.app-select__trigger:focus-visible {
  border-color: #5b7edb;
  outline: none;
}

.app-select__trigger--disabled {
  background: #f8f8f8;
  cursor: not-allowed;
}

.app-select__trigger-text {
  flex: 1;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #4c4c4c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-select__trigger-text--placeholder {
  color: #999999;
}

.app-select__chevron {
  font-size: 18px;
  color: #666666;
  flex-shrink: 0;
}

.app-select__menu-wrap {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(102, 102, 102, 0.12);
  overflow: hidden;
}

.app-select__search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #ededed;
}

.app-select__search i {
  font-size: 16px;
  color: #999999;
  flex-shrink: 0;
}

.app-select__search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #4c4c4c;
}

.app-select__search-input::placeholder {
  color: #999999;
}

.app-select__menu {
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 0;
}

.app-select__menu-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  color: #4c4c4c;
  text-align: left;
  cursor: pointer;
}

.app-select__menu-item:hover {
  background: #f8f8f8;
}

.app-select__menu-item--selected {
  background: #f0f4ff;
  color: #5b7edb;
}

.app-select__menu-status {
  margin: 0;
  padding: 10px 16px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
  color: #999999;
  text-align: center;
}
</style>

<style>
.app-select__menu-content {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}
</style>
